// src/components/tours/MusicianDashboardTour.jsx
// Simple custom tour implementation compatible with React 19 with draggable popups and bold highlighting

import React, { useState, useEffect, useRef } from 'react';
import { auth, db } from '../../config/firebase-config';
import { doc, updateDoc, getDoc, setDoc } from 'firebase/firestore';
import { Music, DollarSign, Star, Sparkles, X, ArrowRight, ArrowLeft, Move } from 'lucide-react';

const MusicianDashboardTour = ({ onComplete, onTabChange }) => {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [hasSeenTour, setHasSeenTour] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const tooltipRef = useRef(null);
  const [demoMode, setDemoMode] = useState(false);
  const [typewriterText, setTypewriterText] = useState('');
  const typewriterTimeouts = useRef([]);
  
  // Dragging state
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [hasBeenDragged, setHasBeenDragged] = useState(false);

  // Define tour steps with highlighting
  const steps = [
    {
      target: 'body',
      title: 'Welcome to Your Musician Dashboard! 🎵',
      content: "Let's take a quick tour to help you start earning money with your musical talent. This will only take 2 minutes!",
      icon: Sparkles,
      placement: 'center',
      showOverlay: true,
      tab: 'earnings',
      highlights: []
    },
    {
      target: '.available-balance',
      title: '💰 Track Your Earnings',
      content: 'Monitor your available balance, request earnings, referral earnings and total earnings. Once you accumulate more than $20, you can redeem your earnings through vouchers.',
      tip: '💡 Tip: Complete requests quickly for bonus rewards!',
      icon: DollarSign,
      placement: 'bottom',
      tab: 'earnings',
      demoData: {
        availableBalance: 350,
        requestEarnings: 285,
        referralEarnings: 65,
        totalEarnings: 350
      },
      highlights: [
        { selector: '.available-balance', style: 'pulse-strong' },
        { selector: '.request-earnings', style: 'glow' },
        { selector: '.referral-earnings', style: 'glow' },
        { selector: '.total-earnings', style: 'pulse' }
      ]
    },
    {
      target: '.song-library',
      title: '🎵 Your Song Library',
      content: 'Enter the details of songs you are offering as requests. You can input one at a time or use bulk upload for faster setup.',
      tip: '🎸 Pro tip: Add 20+ songs to get 3x more requests!',
      icon: Music,
      placement: 'bottom',
      tab: 'songs',
      demoData: {
        songName: 'Scar',
        artistName: 'Missy Higgins',
        price: '10'
      },
      typewriter: true,
      highlights: [
        { selector: '.add-new-song-btn', style: 'pulse-strong' },
        { selector: '.song-name-input', style: 'glow' },
        { selector: '.artist-input', style: 'glow' },
        { selector: '.price-input', style: 'glow' },
        { selector: '.bulk-upload-csv-btn', style: 'pulse-strong' },
        { selector: '.song-count', style: 'pulse' }
      ]
    },
    {
      target: '.qr-code-display',
      title: '📱 Your QR Code',
      content: 'Your unique QR code lets audiences connect to your profile and make requests. Customize colors and download print-ready files!',
      tip: '🎨 Pro tip: Place QR codes on your equipment or merch for more visibility!',
      icon: Music,
      placement: 'bottom',
      tab: 'qrcode',
      scrollToTarget: true,
      fallbackTargets: ['.qr-section', '.qr-display', '[data-qr]', 'canvas'],
      highlights: [
        { selector: '.qr-code-display', style: 'glow' },
        { selector: '.download-qr-btn', style: 'pulse' },
        { selector: '.color-picker', style: 'border' }
      ]
    },
    {
      target: 'body',
      title: '🎨 Print Materials - A5 Flyers',
      content: 'Watch as we select an A5 flyer and show you how easy it is to customize colors and designs! This demonstrates the professional QR code materials you can create.',
      tip: '📄 Pro tip: These materials help fans easily connect to your music at any venue!',
      icon: Music,
      placement: 'right',
      tab: 'qrcode',
      scrollToTarget: false,
      customScroll: true,
      autoClickFlyer: true,
      productAnimation: true,
      allowNavigation: true,
      highlights: []
    },
    {
      target: 'h2[style*="💎"]',
      title: '💎 Referral Program',
      content: 'Build your own network! Share your referral code with other musicians and earn 2% commission for 12 months! Watch your referral stats grow.',
      checklist: [
        'Earn 2% of their earnings for 12 months',
        'Track your referral network growth',
        'Get bonus rewards for active referrers'
      ],
      icon: Star,
      placement: 'bottom',
      tab: 'referrals',
      demoData: {
        totalReferrals: 10,
        activeReferrals: 10,
        referralEarnings: 50
      },
      referralAnimation: true,
      fallbackTargets: [
        'div[style*="borderRadius: 20px"] h2',
        'h2:contains("Referral Program")',
        'div[style*="#1F2937"] h2',
        'div[style*="#10B981"]:first'
      ],
      highlights: [
        { selector: 'h2', style: 'pulse-strong' },
        { selector: 'div[style*="grid-template-columns"]', style: 'glow' }
      ]
    },
    {
      target: 'div[class*="verification"]',
      title: '✅ Email & SMS Verification',
      content: 'Complete your email and SMS verification to unlock full platform features and receive notifications when fans request songs.',
      checklist: [
        'Verify your email address for account security',
        'Verify your SMS number for song request notifications',
        'Complete profile information for better fan trust'
      ],
      icon: Sparkles,
      placement: 'bottom',
      tab: 'profile',
      fallbackTargets: [
        '[class*="verification"]',
        'button:contains("Verify")',
        'div[style*="background"][style*="border"]',
        'body'
      ],
      highlights: [
        { selector: 'div[class*="verification"]', style: 'pulse-strong' },
        { selector: 'button[class*="verify"]', style: 'glow' }
      ]
    },
    {
      target: 'input[type="checkbox"]',
      title: '🎵 Request System Control',
      content: 'Control when you accept song requests. Turn this on when performing and off during breaks or when not available.',
      checklist: [
        'Toggle requests ON when performing live',
        'Toggle requests OFF during breaks or sound checks',
        'Your request status is shown to fans on your public page'
      ],
      icon: Music,
      placement: 'bottom',
      tab: 'profile',
      fallbackTargets: [
        'input[type="checkbox"]',
        'button[class*="toggle"]',
        'div[class*="switch"]',
        'div[style*="cursor: pointer"]',
        'body'
      ],
      highlights: [
        { selector: 'input[type="checkbox"]', style: 'pulse-strong' }
      ]
    },
    {
      target: 'body',
      title: "🎉 You're All Set!",
      content: "That's everything you need to know to get started! Remember to add your songs, share your QR code with audiences, and watch your earnings grow!",
      tip: '💪 Complete your profile to get 50% more requests!',
      icon: Sparkles,
      placement: 'center',
      showOverlay: true,
      tab: 'earnings',
      highlights: []
    }
  ];

  // Check if user has seen tour
  useEffect(() => {
    const checkTourStatus = async () => {
      if (!auth.currentUser) return;

      try {
        // Check musicians collection first
        const musicianDoc = await getDoc(doc(db, 'musicians', auth.currentUser.uid));
        if (musicianDoc.exists() && musicianDoc.data().hasSeenDashboardTour) {
          setHasSeenTour(true);
          return;
        }

        // Check users collection as fallback
        const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
        if (userDoc.exists() && userDoc.data().hasSeenDashboardTour) {
          setHasSeenTour(true);
          return;
        }

        // If no tour status found, activate it
        setIsActive(true);
      } catch (error) {
        console.error('Error checking tour status:', error);
      }
    };

    checkTourStatus();
  }, []);

  // Apply highlighting to elements
  const applyHighlights = (highlights) => {
    // First, remove all existing highlights
    removeAllHighlights();

    // Apply new highlights
    highlights.forEach(({ selector, style }) => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        element.classList.add('tour-highlight', `tour-highlight-${style}`);
      });
    });
  };

  const removeAllHighlights = () => {
    const highlightedElements = document.querySelectorAll('.tour-highlight');
    highlightedElements.forEach(element => {
      element.classList.remove(
        'tour-highlight',
        'tour-highlight-pulse',
        'tour-highlight-pulse-strong',
        'tour-highlight-glow',
        'tour-highlight-border'
      );
    });
  };

  // Restore original earnings values
  const restoreEarningsValues = () => {
    if (window.tourOriginalValues) {
      const { availableBalance, requestEarnings, referralEarnings, totalEarnings } = window.tourOriginalValues;
      
      if (availableBalance) {
        const el = document.querySelector('.available-balance p:nth-child(3)');
        if (el) {
          el.textContent = availableBalance;
          el.style.animation = '';
          el.style.color = '';
          el.style.fontSize = '';
          el.style.fontWeight = '';
        }
      }
      
      if (requestEarnings) {
        const el = document.querySelector('.request-earnings p:nth-child(3)');
        if (el) {
          el.textContent = requestEarnings;
          el.style.animation = '';
          el.style.color = '';
          el.style.fontSize = '';
          el.style.fontWeight = '';
        }
      }
      
      if (referralEarnings) {
        const el = document.querySelector('.referral-earnings p:nth-child(3)');
        if (el) {
          el.textContent = referralEarnings;
          el.style.animation = '';
          el.style.color = '';
          el.style.fontSize = '';
          el.style.fontWeight = '';
        }
      }
      
      if (totalEarnings) {
        const el = document.querySelector('.total-earnings p:nth-child(3)');
        if (el) {
          el.textContent = totalEarnings;
          el.style.animation = '';
          el.style.color = '';
          el.style.fontSize = '';
          el.style.fontWeight = '';
        }
      }
      
      // Clean up
      delete window.tourOriginalValues;
      console.log('💰 Restored original earnings values');
    }
  };

  // Apply demo data to simulate account growth or typewriter effects
  const applyDemoData = (demoData) => {
    const step = steps[currentStep];
    
    // Handle typewriter effect for song inputs
    if (step.typewriter && demoData.songName && demoData.artistName && demoData.price) {
      applyTypewriterEffect(demoData);
      return;
    }

    // Handle earnings animation with actual demo values - much slower
    if (demoData.availableBalance !== undefined) {
      setTimeout(() => {
        applyEarningsDemoSlowly(demoData);
      }, 1000);
      return;
    }

    // Handle referral animation
    if (step.referralAnimation && demoData.totalReferrals !== undefined) {
      setTimeout(() => {
        applyReferralAnimation(demoData);
      }, 1000);
    }
    
    console.log('🎭 Demo data applied:', demoData);
  };

  // Apply earnings demo with smooth counting animation
  const applyEarningsDemoSlowly = (demoData) => {
    // Store original values for restoration
    const originalValues = {};
    
    // First, find all elements and store original values
    const availableBalanceEl = document.querySelector('.available-balance p:nth-child(3)');
    const requestEarningsEl = document.querySelector('.request-earnings p:nth-child(3)');
    const referralEarningsEl = document.querySelector('.referral-earnings p:nth-child(3)');
    const totalEarningsEl = document.querySelector('.total-earnings p:nth-child(3)');
    
    if (availableBalanceEl && availableBalanceEl.textContent.includes('$')) {
      originalValues.availableBalance = availableBalanceEl.textContent;
    }
    if (requestEarningsEl && requestEarningsEl.textContent.includes('$')) {
      originalValues.requestEarnings = requestEarningsEl.textContent;
    }
    if (referralEarningsEl && referralEarningsEl.textContent.includes('$')) {
      originalValues.referralEarnings = referralEarningsEl.textContent;
    }
    if (totalEarningsEl && totalEarningsEl.textContent.includes('$')) {
      originalValues.totalEarnings = totalEarningsEl.textContent;
    }
    
    // Store for cleanup
    window.tourOriginalValues = originalValues;
    
    console.log('💰 Starting smooth counting animation sequence...');
    
    // Start all animations simultaneously but with slight staggered delays - much faster
    setTimeout(() => {
      // Request earnings - start immediately
      if (requestEarningsEl) {
        requestEarningsEl.style.color = '#F59E0B';
        requestEarningsEl.style.fontSize = '28px';
        requestEarningsEl.style.animation = 'tourGlow 4s ease-in-out infinite';
        animateCounter(requestEarningsEl, 0, demoData.requestEarnings, 5000, 'request earnings');
      }
    }, 1000);
    
    // Referral earnings - start 1 second later
    setTimeout(() => {
      if (referralEarningsEl) {
        referralEarningsEl.style.color = '#8B5CF6';
        referralEarningsEl.style.fontSize = '28px';
        referralEarningsEl.style.animation = 'tourGlow 4s ease-in-out infinite';
        animateCounter(referralEarningsEl, 0, demoData.referralEarnings, 4000, 'referral earnings');
      }
    }, 2000);
    
    // Available balance - start 2 seconds later
    setTimeout(() => {
      if (availableBalanceEl) {
        availableBalanceEl.style.color = '#10B981';
        availableBalanceEl.style.fontSize = '28px';
        availableBalanceEl.style.animation = 'tourPulseStrong 4s ease-in-out';
        animateCounter(availableBalanceEl, 0, demoData.availableBalance, 5000, 'available balance');
      }
    }, 3000);
    
    // Total earnings - start 3 seconds later (overlapping with others)
    setTimeout(() => {
      if (totalEarningsEl) {
        totalEarningsEl.style.color = '#06B6D4';
        totalEarningsEl.style.fontSize = '32px';
        totalEarningsEl.style.fontWeight = 'bold';
        totalEarningsEl.style.animation = 'tourPulse 4s ease-in-out infinite';
        animateCounter(totalEarningsEl, 0, demoData.totalEarnings, 5000, 'total earnings', () => {
          console.log('🎉 All earnings animations running simultaneously!');
        });
      }
    }, 4000);
  };

  // Smooth counter animation function
  const animateCounter = (element, startValue, endValue, duration, label, onComplete) => {
    const startTime = performance.now();
    const difference = endValue - startValue;
    
    const updateCounter = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Use easing function for smooth animation (ease-out)
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      
      const currentValue = startValue + (difference * easedProgress);
      element.textContent = `$${currentValue.toFixed(2)}`;
      
      if (progress < 1) {
        requestAnimationFrame(updateCounter);
      } else {
        // Animation complete
        element.textContent = `$${endValue.toFixed(2)}`;
        console.log(`💰 ${label} animation complete: $${endValue.toFixed(2)}`);
        if (onComplete) onComplete();
      }
    };
    
    console.log(`💰 Starting ${label} counter: $${startValue.toFixed(2)} → $${endValue.toFixed(2)}`);
    requestAnimationFrame(updateCounter);
  };

  // Typewriter effect for song inputs
  const applyTypewriterEffect = (demoData) => {
    const songNameInput = document.querySelector('.song-name-input');
    const artistInput = document.querySelector('.artist-input');
    const priceInput = document.querySelector('.price-input');

    if (!songNameInput || !artistInput || !priceInput) {
      console.warn('Song input fields not found for typewriter effect');
      return;
    }

    // Clear any existing timeouts
    typewriterTimeouts.current.forEach(timeout => clearTimeout(timeout));
    typewriterTimeouts.current = [];

    // Clear the inputs first
    songNameInput.value = '';
    artistInput.value = '';
    priceInput.value = '';

    // Start typewriter sequence
    typewriterSequence([
      { element: songNameInput, text: demoData.songName, delay: 500 },
      { element: artistInput, text: demoData.artistName, delay: 1500 },
      { element: priceInput, text: demoData.price, delay: 3000 }
    ]);
  };

  // Typewriter sequence function
  const typewriterSequence = (sequence) => {
    sequence.forEach(({ element, text, delay }) => {
      const timeout = setTimeout(() => {
        typewriterAnimation(element, text, 100); // 100ms per character
      }, delay);
      typewriterTimeouts.current.push(timeout);
    });
  };

  // Individual typewriter function for one element
  const typewriterAnimation = (element, text, speed = 100) => {
    let i = 0;
    const typeNext = () => {
      if (i < text.length) {
        element.value += text.charAt(i);
        element.dispatchEvent(new Event('input', { bubbles: true })); // Trigger input event for React
        i++;
        const timeout = setTimeout(typeNext, speed);
        typewriterTimeouts.current.push(timeout);
      }
    };
    typeNext();
  };

  // Product animation for A5 flyer - rotate through colors, backgrounds, and styles
  const startProductAnimation = () => {
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD'];
    const textColors = ['#FFFFFF', '#000000', '#333333', '#FFFFFF', '#333333', '#FFFFFF'];
    const accentColors = ['#F59E0B', '#7C3AED', '#EF4444', '#10B981', '#3B82F6', '#EC4899'];
    const backgroundStyles = ['gradient', 'geometric', 'waves', 'dots', 'minimal'];
    
    let colorIndex = 0;
    
    const animateProduct = () => {
      // Find the color input elements and background style selector
      const primaryColorInput = document.querySelector('input[type="color"][value*="#"]');
      const textColorInput = document.querySelectorAll('input[type="color"]')[1];
      const accentColorInput = document.querySelectorAll('input[type="color"]')[2];
      const backgroundStyleSelect = document.querySelector('select[value*="gradient"], select[value*="geometric"], select[value*="waves"]') ||
                                   Array.from(document.querySelectorAll('select')).find(sel => 
                                     sel.innerHTML.includes('gradient') || sel.innerHTML.includes('geometric') || 
                                     sel.innerHTML.includes('Background') || sel.innerHTML.includes('Style'));
      
      if (primaryColorInput && textColorInput) {
        // Animate the color inputs
        const newPrimaryColor = colors[colorIndex];
        const newTextColor = textColors[colorIndex];
        const newAccentColor = accentColors[colorIndex];
        const newBackgroundStyle = backgroundStyles[colorIndex % backgroundStyles.length];
        
        // Update the input values
        primaryColorInput.value = newPrimaryColor;
        textColorInput.value = newTextColor;
        if (accentColorInput) {
          accentColorInput.value = newAccentColor;
        }
        
        // Update background style if found
        if (backgroundStyleSelect) {
          backgroundStyleSelect.value = newBackgroundStyle;
          backgroundStyleSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
        
        // Trigger change events to update the preview
        primaryColorInput.dispatchEvent(new Event('input', { bubbles: true }));
        primaryColorInput.dispatchEvent(new Event('change', { bubbles: true }));
        textColorInput.dispatchEvent(new Event('input', { bubbles: true }));
        textColorInput.dispatchEvent(new Event('change', { bubbles: true }));
        if (accentColorInput) {
          accentColorInput.dispatchEvent(new Event('input', { bubbles: true }));
          accentColorInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        
        // Add visual feedback to the inputs
        primaryColorInput.style.transform = 'scale(1.1)';
        textColorInput.style.transform = 'scale(1.1)';
        primaryColorInput.style.border = '3px solid #7C3AED';
        textColorInput.style.border = '3px solid #7C3AED';
        if (accentColorInput) {
          accentColorInput.style.transform = 'scale(1.1)';
          accentColorInput.style.border = '3px solid #7C3AED';
        }
        if (backgroundStyleSelect) {
          backgroundStyleSelect.style.border = '3px solid #7C3AED';
        }
        
        setTimeout(() => {
          if (primaryColorInput && textColorInput) {
            primaryColorInput.style.transform = 'scale(1)';
            textColorInput.style.transform = 'scale(1)';
            primaryColorInput.style.border = '2px solid #4B5563';
            textColorInput.style.border = '2px solid #4B5563';
            if (accentColorInput) {
              accentColorInput.style.transform = 'scale(1)';
              accentColorInput.style.border = '2px solid #4B5563';
            }
            if (backgroundStyleSelect) {
              backgroundStyleSelect.style.border = '2px solid #4B5563';
            }
          }
        }, 800);
        
        console.log(`🎨 Applied design ${colorIndex + 1}: ${newPrimaryColor} / ${newTextColor} / ${newBackgroundStyle}`);
        colorIndex = (colorIndex + 1) % colors.length;
      } else {
        console.warn('🎨 Could not find color input elements for animation');
      }
    };
    
    // Start animation cycle and store interval for cleanup
    setTimeout(() => animateProduct(), 1000); // First change after 1 second
    const animationInterval = setInterval(animateProduct, 2500); // Then every 2.5 seconds
    
    // Store interval globally so it can be cleared when user moves to next step
    window.tourProductAnimationInterval = animationInterval;
    
    console.log('🎨 Product animation started - will continue until next step');
  };

  // Apply referral animation with counting numbers
  const applyReferralAnimation = (demoData) => {
    console.log('💎 Starting referral animation with data:', demoData);
    
    // Find referral stat elements using more specific targeting
    let totalReferralsEl = null;
    let activeReferralsEl = null; 
    let referralEarningsEl = null;

    // Look for the specific stat card structure
    const statCards = document.querySelectorAll('div[style*="borderRadius"][style*="padding"][style*="textAlign: center"]');
    
    statCards.forEach(card => {
      const text = card.textContent;
      
      // Check if this card contains "Total Referrals"
      if (text.includes('Total Referrals') && !totalReferralsEl) {
        // Look for the number div (32px font, bold, colored)
        const numberEl = card.querySelector('div[style*="fontSize: \'32px\'"], div[style*="fontWeight: \'bold\'"]');
        if (numberEl && /^\d+$/.test(numberEl.textContent.trim())) {
          totalReferralsEl = numberEl;
          console.log('💎 Found Total Referrals number element:', numberEl.textContent);
        }
      }
      
      // Check if this card contains "Active Referrals"
      if (text.includes('Active Referrals') && !activeReferralsEl) {
        const numberEl = card.querySelector('div[style*="fontSize: \'32px\'"], div[style*="fontWeight: \'bold\'"]');
        if (numberEl && /^\d+$/.test(numberEl.textContent.trim())) {
          activeReferralsEl = numberEl;
          console.log('💎 Found Active Referrals number element:', numberEl.textContent);
        }
      }
      
      // Check if this card contains earnings ($ symbol)
      if (text.includes('$') && !referralEarningsEl) {
        const numberEl = card.querySelector('div[style*="fontSize: \'32px\'"], div[style*="fontWeight: \'bold\'"]');
        if (numberEl && numberEl.textContent.includes('$')) {
          referralEarningsEl = numberEl;
          console.log('💎 Found Referral Earnings element:', numberEl.textContent);
        }
      }
    });

    // Additional fallback - look for elements with specific styles and nearby text
    if (!totalReferralsEl || !activeReferralsEl) {
      const allBoldNumbers = document.querySelectorAll('div[style*="fontWeight: bold"], div[style*="fontWeight: \'bold\'"]');
      allBoldNumbers.forEach(el => {
        const parentText = el.parentElement?.textContent || '';
        const text = el.textContent.trim();
        
        if (!totalReferralsEl && parentText.includes('Total Referrals') && /^\d+$/.test(text)) {
          totalReferralsEl = el;
          console.log('💎 Fallback found Total Referrals:', text);
        }
        
        if (!activeReferralsEl && parentText.includes('Active Referrals') && /^\d+$/.test(text)) {
          activeReferralsEl = el;
          console.log('💎 Fallback found Active Referrals:', text);
        }
      });
    }

    console.log('💎 Final elements found:', { 
      totalReferralsEl: totalReferralsEl?.textContent, 
      activeReferralsEl: activeReferralsEl?.textContent, 
      referralEarningsEl: referralEarningsEl?.textContent 
    });

    // Start animations with delays and better debugging
    setTimeout(() => {
      if (totalReferralsEl) {
        console.log('💎 Starting Total Referrals animation');
        animateReferralStat(totalReferralsEl, 0, demoData.totalReferrals, 3000, 'Total Referrals', false);
      } else {
        console.warn('💎 Total Referrals element not found - trying manual search');
        // Manual fallback search
        const manualEl = Array.from(document.querySelectorAll('*')).find(el => {
          const parent = el.parentElement;
          return parent && parent.textContent.includes('Total Referrals') && 
                 /^\d+$/.test(el.textContent.trim()) && el.textContent.trim() !== '';
        });
        if (manualEl) {
          console.log('💎 Found manual Total Referrals element:', manualEl.textContent);
          animateReferralStat(manualEl, 0, demoData.totalReferrals, 3000, 'Total Referrals', false);
        }
      }
    }, 1000);

    setTimeout(() => {
      if (activeReferralsEl) {
        console.log('💎 Starting Active Referrals animation');
        animateReferralStat(activeReferralsEl, 0, demoData.activeReferrals, 3000, 'Active Referrals', false);
      } else {
        console.warn('💎 Active Referrals element not found - trying manual search');
        // Manual fallback search
        const manualEl = Array.from(document.querySelectorAll('*')).find(el => {
          const parent = el.parentElement;
          return parent && parent.textContent.includes('Active Referrals') && 
                 /^\d+$/.test(el.textContent.trim()) && el.textContent.trim() !== '';
        });
        if (manualEl) {
          console.log('💎 Found manual Active Referrals element:', manualEl.textContent);
          animateReferralStat(manualEl, 0, demoData.activeReferrals, 3000, 'Active Referrals', false);
        }
      }
    }, 2000);

    setTimeout(() => {
      if (referralEarningsEl) {
        console.log('💎 Starting Referral Earnings animation');
        animateReferralStat(referralEarningsEl, 0, demoData.referralEarnings, 4000, 'Referral Earnings', true);
      } else {
        console.warn('💎 Referral Earnings element not found');
        // Try to find any $ element in referral section
        const dollarEls = Array.from(document.querySelectorAll('*')).filter(el => 
          el.textContent.includes('$') && el.textContent.includes('0'));
        console.log('💎 Found dollar elements:', dollarEls.map(el => el.textContent));
        if (dollarEls.length > 0) {
          animateReferralStat(dollarEls[0], 0, demoData.referralEarnings, 4000, 'Referral Earnings', true);
        }
      }
    }, 3000);
  };

  // Animate referral stat numbers
  const animateReferralStat = (element, startValue, endValue, duration, label, isCurrency) => {
    if (!element) {
      console.warn(`💎 No element found for ${label} animation`);
      return;
    }

    // Store original content for restoration
    const originalContent = element.textContent;
    window.tourOriginalReferralValues = window.tourOriginalReferralValues || {};
    window.tourOriginalReferralValues[label] = originalContent;

    const startTime = performance.now();
    const difference = endValue - startValue;
    
    const updateStat = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Use easing function for smooth animation
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      const currentValue = startValue + (difference * easedProgress);
      
      // Update the display
      const displayValue = Math.round(currentValue);
      const formattedValue = isCurrency ? `$${displayValue.toFixed(2)}` : displayValue.toString();
      
      // Update element content
      element.textContent = formattedValue;
      
      // Add visual effects
      element.style.color = '#10B981';
      element.style.fontSize = '1.5em';
      element.style.fontWeight = 'bold';
      element.style.animation = 'tourPulse 2s ease-in-out infinite';
      element.style.textShadow = '0 0 10px rgba(16, 185, 129, 0.5)';
      element.style.transition = 'all 0.3s ease';
      
      if (progress < 1) {
        requestAnimationFrame(updateStat);
      } else {
        console.log(`💎 ${label} animation complete: ${formattedValue}`);
      }
    };
    
    console.log(`💎 Starting ${label} counter: ${startValue} → ${endValue}`);
    requestAnimationFrame(updateStat);
  };

  // Restore referral values
  const restoreReferralValues = () => {
    console.log('💎 Starting referral values restoration');
    
    if (window.tourOriginalReferralValues) {
      Object.entries(window.tourOriginalReferralValues).forEach(([label, originalValue]) => {
        console.log(`💎 Restoring ${label} to ${originalValue}`);
        
        // Find elements that might match this label and restore them
        if (label.includes('Total Referrals')) {
          const elements = document.querySelectorAll('div[style*="fontSize: \'32px\'"], div[style*="fontWeight: \'bold\'"]');
          elements.forEach(el => {
            const parentText = el.parentElement?.textContent || '';
            if (parentText.includes('Total Referrals')) {
              el.textContent = originalValue;
              el.style.color = '';
              el.style.fontSize = '';
              el.style.fontWeight = '';
              el.style.animation = '';
              el.style.textShadow = '';
              el.style.transition = '';
            }
          });
        }
        
        if (label.includes('Active Referrals')) {
          const elements = document.querySelectorAll('div[style*="fontSize: \'32px\'"], div[style*="fontWeight: \'bold\'"]');
          elements.forEach(el => {
            const parentText = el.parentElement?.textContent || '';
            if (parentText.includes('Active Referrals')) {
              el.textContent = originalValue;
              el.style.color = '';
              el.style.fontSize = '';
              el.style.fontWeight = '';
              el.style.animation = '';
              el.style.textShadow = '';
              el.style.transition = '';
            }
          });
        }
        
        if (label.includes('Referral Earnings')) {
          const elements = document.querySelectorAll('div[style*="fontSize: \'32px\'"], div[style*="fontWeight: \'bold\'"]');
          elements.forEach(el => {
            const parentText = el.parentElement?.textContent || '';
            if ((parentText.includes('Total Earned') || parentText.includes('Earnings')) && el.textContent.includes('$')) {
              el.textContent = originalValue;
              el.style.color = '';
              el.style.fontSize = '';
              el.style.fontWeight = '';
              el.style.animation = '';
              el.style.textShadow = '';
              el.style.transition = '';
            }
          });
        }
      });
      
      delete window.tourOriginalReferralValues;
      console.log('💎 Restored original referral values');
    } else {
      console.log('💎 No original referral values to restore');
    }
    
    // Additional cleanup to prevent blank screen
    const allAnimatedElements = document.querySelectorAll('*[style*="animation"]');
    allAnimatedElements.forEach(el => {
      if (el.style.animation && el.style.animation.includes('tour')) {
        el.style.animation = '';
        el.style.color = '';
        el.style.fontSize = '';
        el.style.textShadow = '';
        el.style.transition = '';
      }
    });
  };

  // Handle step changes and tab navigation
  useEffect(() => {
    if (!isActive) return;

    const step = steps[currentStep];
    if (!step) return;

    // Navigate to the appropriate tab if needed
    if (step.tab && onTabChange) {
      onTabChange(step.tab);
    }

    // Reset drag state when changing steps
    setHasBeenDragged(false);
    setDragOffset({ x: 0, y: 0 });

    // Wait for tab change to complete, then calculate position and apply highlights
    const timer = setTimeout(() => {
      calculatePosition();
      if (step.highlights && step.highlights.length > 0) {
        applyHighlights(step.highlights);
      }
      // Apply demo data if available
      if (step.demoData) {
        applyDemoData(step.demoData);
      }
      
      // Handle auto-click functionality
      if (step.autoClick) {
        setTimeout(() => {
          const clickTarget = document.querySelector(step.autoClick);
          if (clickTarget) {
            clickTarget.click();
            console.log('🎯 Auto-clicked:', step.autoClick);
          }
        }, 1000);
      }

      // Handle auto-click flyer
      if (step.autoClickFlyer) {
        setTimeout(() => {
          // Try multiple approaches to find and click a flyer
          let flyerCard = null;
          
          // Approach 1: Look for flyer cards by emoji
          const allCards = document.querySelectorAll('div[style*="cursor: pointer"]');
          flyerCard = Array.from(allCards).find(card => {
            const text = card.textContent || '';
            return text.includes('📋') || text.includes('A5');
          });
          
          // Approach 2: Look under flyers section
          if (!flyerCard) {
            const flyersSection = document.querySelector('h5:contains("📋 Flyers")');
            if (flyersSection) {
              const flyersContainer = flyersSection.nextElementSibling;
              if (flyersContainer) {
                flyerCard = flyersContainer.querySelector('div[style*="cursor: pointer"]');
              }
            }
          }
          
          // Approach 3: Generic fallback - find any product card
          if (!flyerCard) {
            flyerCard = document.querySelector('div[style*="grid"] div[style*="cursor: pointer"]');
          }
          
          if (flyerCard) {
            flyerCard.click();
            console.log('🎯 Auto-clicked flyer product card');
            
            // Also try to scroll to show the design options that appear
            setTimeout(() => {
              // Find design section using valid selectors
              const designSection = Array.from(document.querySelectorAll('h4')).find(h => 
                                      h.textContent.includes('Live Preview')) ||
                                    document.querySelector('div[style*="Design Customization"]') ||
                                    document.querySelector('input[type="color"]');
              if (designSection) {
                designSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                console.log('📄 Scrolled to design preview section');
              }
            }, 1000);
          } else {
            console.warn('🎯 Could not find any product card to click');
          }
        }, 2000); // Give more time for page to load
      }
      
      // Handle product animation
      if (step.productAnimation) {
        setTimeout(() => startProductAnimation(), 2000); // Delay to let flyer selection complete
      }
      
      // Handle custom scroll for print materials
      if (step.customScroll) {
        setTimeout(() => {
          // Try multiple approaches to find and center the print materials section
          let printSection = null;
          
          // Look for the flyers section specifically
          const flyersHeader = Array.from(document.querySelectorAll('h5')).find(h => 
            h.textContent.includes('📋') || h.textContent.includes('Flyers')
          );
          
          if (flyersHeader) {
            printSection = flyersHeader;
          } else {
            // Look for print materials header
            printSection = Array.from(document.querySelectorAll('h3, h4')).find(h => 
              h.textContent.includes('Print') || h.textContent.includes('Materials')
            );
          }
          
          if (printSection) {
            // Scroll down 40% more to show lower part of flyers section
            const rect = printSection.getBoundingClientRect();
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const targetY = rect.top + scrollTop - (window.innerHeight / 4) + 560; // Additional 40% more (400px -> 560px)
            
            window.scrollTo({
              top: targetY,
              behavior: 'smooth'
            });
            console.log('📄 Scrolled down 40% more to show lower flyers section');
          } else {
            // Fallback: scroll down much more for A5 flyers lower section
            window.scrollTo({
              top: window.scrollY + 1400, // 40% more than 1200px
              behavior: 'smooth'
            });
            console.log('📄 Fallback scroll further down for lower flyers section');
          }
        }, 500);
      }
      
      // Scroll to target
      if (step.scrollToTarget && !step.customScroll) {
        setTimeout(() => scrollTargetIntoView(), 500);
      } else if (!step.customScroll) {
        scrollTargetIntoView();
      }
    }, 300);

    return () => {
      clearTimeout(timer);
      removeAllHighlights();
    };
  }, [currentStep, isActive]);

  // Scroll target into view if needed
  const scrollTargetIntoView = () => {
    const step = steps[currentStep];
    if (!step || step.target === 'body') return;

    const targetElement = document.querySelector(step.target);
    if (!targetElement) return;

    const rect = targetElement.getBoundingClientRect();
    const isInView = rect.top >= 0 && rect.bottom <= window.innerHeight;

    if (!isInView) {
      targetElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  };

  // Recalculate position on window resize (only if not dragged)
  useEffect(() => {
    const handleResize = () => {
      if (!hasBeenDragged) {
        calculatePosition();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [hasBeenDragged, currentStep]);

  const calculatePosition = () => {
    const step = steps[currentStep];
    if (!step || !tooltipRef.current || hasBeenDragged) return;

    // Handle body/center placement - check if it's the intro step
    if (step.target === 'body' || step.placement === 'center') {
      // Step 1 (intro) should be centered
      if (currentStep === 0) {
        setTooltipPosition({
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          position: 'fixed',
          zIndex: 9999
        });
      } else {
        // Other body/center steps go to right side
        setTooltipPosition({
          top: '15%',
          right: '20px',
          transform: 'none',
          position: 'fixed',
          zIndex: 9999
        });
      }
      return;
    }

    // Find target element
    let targetElement = document.querySelector(step.target);
    if (!targetElement) {
      console.warn(`Tour target not found: ${step.target}`);
      
      // Try fallback targets if specified
      if (step.fallbackTargets) {
        for (const fallback of step.fallbackTargets) {
          targetElement = document.querySelector(fallback);
          if (targetElement) {
            console.log(`✅ Found fallback target: ${fallback}`);
            break;
          }
        }
      }
      
      // If still no target found, skip step
      if (!targetElement) {
        console.warn(`❌ No target found for step ${currentStep + 1}, moving to next step`);
        nextStep();
        return;
      }
    }

    const rect = targetElement.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const tooltipWidth = tooltipRect.width || 400;
    const tooltipHeight = tooltipRect.height || 200;

    let top = 0;
    let left = 0;

    // Special positioning for specific problematic steps
    if (currentStep === 1) { // Step 2 - earnings (move left 25% more)
      top = rect.bottom - (tooltipHeight * 0.75) - 50; // 75% up
      left = rect.left + (rect.width / 2) - (tooltipWidth * 1.75); // 125% more left (100% + 25%)
    } else if (currentStep === 2) { // Step 3 - songs (move left 300% and up 100%)
      top = rect.top - tooltipHeight - 20; // 100% up (above target)
      left = rect.left - (tooltipWidth * 2.0); // 300% left
    } else if (currentStep === 3) { // Step 4 - QR code (move 200% left and 100% up)
      top = rect.top - tooltipHeight - 20; // 100% up (above target)
      left = rect.left - (tooltipWidth * 1.0); // 200% left
    } else if (currentStep === 4) { // Step 5 - print materials (move to left side)
      top = rect.top + (rect.height / 2) - (tooltipHeight / 2);
      left = rect.left - tooltipWidth - 30;
    } else if (currentStep === 5) { // Step 6 - referrals (move to right side)
      top = rect.top + (rect.height / 2) - (tooltipHeight / 2);
      left = rect.right + 30;
    } else if (currentStep === 6) { // Step 7 - verification (move left 100%)
      top = rect.top + (rect.height / 2) - (tooltipHeight / 2);
      left = rect.left - tooltipWidth; // 100% left
    } else if (currentStep === 7) { // Step 8 - request system control (move left 100%) 
      top = rect.top + (rect.height / 2) - (tooltipHeight / 2);
      left = rect.left - tooltipWidth; // 100% left
    } else {
      // Default positioning logic
      switch (step.placement) {
        case 'bottom':
          top = rect.bottom + 20;
          left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
          break;
        case 'top':
          top = rect.top - tooltipHeight - 20;
          left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
          break;
        case 'right':
          top = rect.top + (rect.height / 2) - (tooltipHeight / 2);
          left = rect.right + 20;
          break;
        case 'left':
          top = rect.top + (rect.height / 2) - (tooltipHeight / 2);
          left = rect.left - tooltipWidth - 20;
          break;
        default:
          top = rect.bottom + 20;
          left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
      }
    }

    // Keep tooltip within viewport
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    if (left < 20) left = 20;
    if (left + tooltipWidth > viewportWidth - 20) left = viewportWidth - tooltipWidth - 20;
    if (top < 20) top = 20;
    if (top + tooltipHeight > viewportHeight - 20) top = viewportHeight - tooltipHeight - 20;

    setTooltipPosition({
      top: `${top}px`,
      left: `${left}px`,
      position: 'fixed',
      transform: 'none'
    });

    // Highlight target element
    targetElement.style.position = 'relative';
    targetElement.style.zIndex = '10001';
    targetElement.style.boxShadow = '0 0 0 4px rgba(124, 58, 237, 0.5), 0 0 20px rgba(124, 58, 237, 0.3)';
    targetElement.style.borderRadius = '8px';
  };

  const clearHighlight = () => {
    const step = steps[currentStep];
    if (step && step.target !== 'body') {
      const targetElement = document.querySelector(step.target);
      if (targetElement) {
        targetElement.style.position = '';
        targetElement.style.zIndex = '';
        targetElement.style.boxShadow = '';
        targetElement.style.borderRadius = '';
      }
    }
    removeAllHighlights();
    
    // Restore earnings values when leaving the earnings step
    if (step && step.tab === 'earnings' && step.demoData) {
      restoreEarningsValues();
    }
    
    // Restore referral values when leaving the referrals step
    if (step && step.tab === 'referrals' && step.referralAnimation) {
      restoreReferralValues();
    }
    
    // Clear product animations when leaving print step
    if (step && step.productAnimation && window.tourProductAnimationInterval) {
      clearInterval(window.tourProductAnimationInterval);
      window.tourProductAnimationInterval = null;
      console.log('🎨 Stopped product animation');
    }
  };

  // Drag handlers
  const handleMouseDown = (e) => {
    // Only allow dragging from the header area
    if (e.target.closest('.tour-header')) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y
      });
      e.preventDefault();
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;

    setDragOffset({ x: newX, y: newY });
    setHasBeenDragged(true);

    // Update position with drag offset
    const step = steps[currentStep];
    if (step.placement === 'center' || step.target === 'body') {
      if (currentStep === 0) {
        // Step 1 (intro) - maintain center drag behavior
        setTooltipPosition({
          top: `calc(50% + ${newY}px)`,
          left: `calc(50% + ${newX}px)`,
          transform: 'translate(-50%, -50%)',
          position: 'fixed',
          zIndex: 9999
        });
      } else {
        // Other body/center steps
        setTooltipPosition({
          top: `calc(15% + ${newY}px)`,
          right: `calc(20px - ${newX}px)`,
          transform: 'none',
          position: 'fixed',
          zIndex: 9999
        });
      }
    } else {
      const currentPos = tooltipRef.current?.getBoundingClientRect();
      if (currentPos) {
        setTooltipPosition({
          top: `${e.clientY - (currentPos.height / 2)}px`,
          left: `${e.clientX - (currentPos.width / 2)}px`,
          position: 'fixed',
          transform: 'none'
        });
      }
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Add global mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragStart]);

  const nextStep = () => {
    const currentStepData = steps[currentStep];
    
    // Clean up current step before moving
    clearHighlight();
    
    // Clear any typewriter timeouts
    typewriterTimeouts.current.forEach(timeout => clearTimeout(timeout));
    typewriterTimeouts.current = [];
    
    // Force cleanup of referral animations to prevent blank screens
    if (currentStepData && currentStepData.referralAnimation) {
      console.log('💎 Forcing referral cleanup on step transition');
      restoreReferralValues();
      
      // Additional cleanup for any lingering animations
      const allAnimatedElements = document.querySelectorAll('*[style*="animation"], *[style*="tourPulse"], *[style*="tourGlow"]');
      allAnimatedElements.forEach(el => {
        if (el.style.animation && el.style.animation.includes('tour')) {
          el.style.animation = '';
          el.style.color = '';
          el.style.fontSize = '';
          el.style.textShadow = '';
          el.style.transition = '';
        }
      });
    }
    
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      completeTour();
    }
  };

  const prevStep = () => {
    clearHighlight();
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const skipTour = () => {
    clearHighlight();
    completeTour(false);
  };

  const completeTour = async (completed = true) => {
    clearHighlight();
    
    // Clear any running typewriter effects
    typewriterTimeouts.current.forEach(timeout => clearTimeout(timeout));
    typewriterTimeouts.current = [];
    
    // Ensure earnings values are restored
    restoreEarningsValues();
    
    setIsActive(false);
    
    if (auth.currentUser) {
      const tourCompletionData = {
        hasSeenDashboardTour: true,
        tourCompletedAt: new Date(),
        tourStatus: completed ? 'completed' : 'skipped'
      };

      try {
        // Try to save to musicians collection first (most likely correct)
        try {
          await updateDoc(doc(db, 'musicians', auth.currentUser.uid), tourCompletionData);
          console.log('✅ Tour status saved to musicians collection');
        } catch (musicianError) {
          console.log('Failed to save to musicians collection, trying users collection');
          // Fallback to users collection
          await updateDoc(doc(db, 'users', auth.currentUser.uid), tourCompletionData);
          console.log('✅ Tour status saved to users collection');
        }
      } catch (error) {
        console.error('❌ Error saving tour status to both collections:', error);
        // Create the document if it doesn't exist
        try {
          await setDoc(doc(db, 'musicians', auth.currentUser.uid), tourCompletionData, { merge: true });
          console.log('✅ Tour status created in musicians collection');
        } catch (createError) {
          console.error('❌ Failed to create tour status document:', createError);
        }
      }
    }

    if (onComplete) {
      onComplete(completed);
    }

    if (completed) {
      console.log('🎉 Tour completed! Welcome to RequestASong!');
    }
  };

  if (!isActive || hasSeenTour || currentStep >= steps.length) {
    return null;
  }

  const step = steps[currentStep];
  const Icon = step.icon;

  return (
    <>
      {/* Overlay */}
      {step.showOverlay && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          zIndex: 10000
        }} />
      )}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        style={{
          ...tooltipPosition,
          zIndex: 10002,
          maxWidth: '400px',
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)',
          border: '4px solid #7C3AED',
          overflow: 'hidden',
          cursor: isDragging ? 'grabbing' : 'default',
          userSelect: 'none'
        }}
        onMouseDown={handleMouseDown}
      >
        {/* Header - Draggable Area */}
        <div 
          className="tour-header"
          style={{
            backgroundColor: '#7C3AED',
            padding: '20px',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: isDragging ? 'grabbing' : 'grab'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Icon size={24} />
            <span style={{ fontSize: '16px', fontWeight: 'bold' }}>
              Step {currentStep + 1} of {steps.length}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Move 
              size={16} 
              style={{ 
                opacity: 0.8,
                animation: hasBeenDragged ? 'none' : 'pulse 2s ease-in-out infinite'
              }} 
              title="Drag to move popup"
            />
            <button
              onClick={skipTour}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                padding: '4px'
              }}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '24px' }}>
          <h3 style={{
            fontSize: '20px',
            fontWeight: 'bold',
            margin: '0 0 12px 0',
            color: '#1F2937'
          }}>
            {step.title}
          </h3>
          
          <p style={{
            color: '#4B5563',
            margin: '0 0 12px 0',
            lineHeight: '1.5'
          }}>
            {step.content}
          </p>

          {step.tip && (
            <p style={{
              color: '#7C3AED',
              fontSize: '14px',
              fontWeight: '600',
              margin: '0 0 16px 0'
            }}>
              {step.tip}
            </p>
          )}

          {step.checklist && (
            <ul style={{
              color: '#4B5563',
              margin: '16px 0',
              paddingLeft: '20px',
              lineHeight: '1.6'
            }}>
              {step.checklist.map((item, index) => (
                <li key={index} style={{ marginBottom: '8px' }}>
                  <span style={{ color: '#10B981', marginRight: '8px' }}>✓</span>
                  {item}
                </li>
              ))}
            </ul>
          )}

          {/* Drag instruction (only show on first few steps) */}
          {currentStep < 2 && !hasBeenDragged && (
            <p style={{
              color: '#9CA3AF',
              fontSize: '12px',
              fontStyle: 'italic',
              margin: '12px 0 0 0',
              padding: '8px',
              backgroundColor: '#F9FAFB',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <Move size={12} />
              Tip: You can drag this popup by its header if it's covering something
            </p>
          )}
        </div>

        {/* Footer */}
        <div style={{
          backgroundColor: '#F9FAFB',
          padding: '16px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderTop: '1px solid #E5E7EB'
        }}>
          <button
            onClick={skipTour}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: '#6B7280',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Skip Tour
          </button>

          <div style={{ display: 'flex', gap: '12px' }}>
            {currentStep > 0 && (
              <button
                onClick={prevStep}
                style={{
                  backgroundColor: '#6B7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <ArrowLeft size={16} />
                Back
              </button>
            )}
            
            <button
              onClick={() => {
                console.log('🎯 Next button clicked from step:', currentStep);
                nextStep();
              }}
              style={{
                backgroundColor: '#7C3AED',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 16px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              {currentStep === steps.length - 1 ? 'Finish' : 'Next'}
              {currentStep < steps.length - 1 && <ArrowRight size={16} />}
            </button>
          </div>
        </div>
      </div>

      {/* Add animation styles */}
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 0.8;
          }
          50% {
            opacity: 0.4;
          }
        }

        @keyframes tourPulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.05);
            opacity: 0.9;
          }
        }

        @keyframes tourPulseStrong {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(124, 58, 237, 0.7);
          }
          50% {
            transform: scale(1.1);
            box-shadow: 0 0 0 10px rgba(124, 58, 237, 0);
          }
        }

        @keyframes tourGlow {
          0%, 100% {
            box-shadow: 0 0 20px rgba(124, 58, 237, 0.6),
                        0 0 40px rgba(124, 58, 237, 0.4),
                        0 0 60px rgba(124, 58, 237, 0.2);
          }
          50% {
            box-shadow: 0 0 30px rgba(124, 58, 237, 0.8),
                        0 0 60px rgba(124, 58, 237, 0.6),
                        0 0 90px rgba(124, 58, 237, 0.4);
          }
        }

        @keyframes borderPulse {
          0%, 100% {
            border-color: rgba(124, 58, 237, 1);
          }
          50% {
            border-color: rgba(124, 58, 237, 0.5);
          }
        }

        .tour-highlight {
          position: relative !important;
          z-index: 10001 !important;
          transition: all 0.3s ease !important;
        }

        .tour-highlight-pulse {
          animation: tourPulse 2s ease-in-out infinite !important;
        }

        .tour-highlight-pulse-strong {
          animation: tourPulseStrong 1.5s ease-in-out infinite !important;
          position: relative !important;
          z-index: 10001 !important;
        }

        .tour-highlight-glow {
          animation: tourGlow 2s ease-in-out infinite !important;
          border-radius: 8px !important;
        }

        .tour-highlight-border {
          border: 3px solid #7C3AED !important;
          animation: borderPulse 1.5s ease-in-out infinite !important;
          border-radius: 8px !important;
        }
      `}</style>
    </>
  );
};

export default MusicianDashboardTour;